/**
 * Point d'entrée pour le nouveau système de modales - VERSION 2.0
 * Export centralisé et API de migration progressive
 */

// Base classes
export { BaseModal } from './BaseModal';
export type { ModalConfig, ModalElements } from './BaseModal';

// Registry
export { ModalRegistry, modalRegistry } from './ModalRegistry';
export type { ModalRegistration } from './ModalRegistry';

// Specialized modals
export { ServiceModal } from './ServiceModal';
export type { ServiceModalData } from './ServiceModal';

// API de migration progressive
export class ModalSystemV2 {
  private static initialized = false;
  private static migrationMode: 'test' | 'parallel' | 'full' = 'test';

  /**
   * Initialise directement le système V2 en production (sans tests)
   */
  public static async initProduction(): Promise<boolean> {
    if (this.initialized) return true;

    console.log('🚀 Initialisation du système de modales V2 en production...');
    
    try {
      // Importer le registry et les modales V2
      const { modalRegistry } = await import('./ModalRegistry');
      const { ServiceModal } = await import('./ServiceModal');
      
      // Créer et enregistrer la modale avec l'ID de production
      const serviceModal = new ServiceModal({ id: 'service-detail-modal' });
      modalRegistry.register(serviceModal, 'service');
      modalRegistry.init('service-detail-modal');

      this.migrationMode = 'full';
      this.initialized = true;
      console.log('✅ Système de modales V2 initialisé en production');
      return true;
    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation en production:', error);
      return false;
    }
  }

  /**
   * Active le mode parallèle (ancien + nouveau système coexistent)
   */
  public static async enableParallelMode(): Promise<boolean> {
    if (!this.initialized) {
      const initSuccess = await this.initProduction();
      if (!initSuccess) return false;
    }

    console.log('🔄 Activation du mode parallèle...');
    
    try {
      // Importer le registry et les modales V2
      const { modalRegistry } = await import('./ModalRegistry');
      const { ServiceModal } = await import('./ServiceModal');
      
      // Créer les modales V2 avec des IDs spécifiques
      const serviceModalV2 = new ServiceModal({ id: 'service-detail-modal-v2' });
      modalRegistry.register(serviceModalV2, 'service');
      
      // Pré-initialiser pour charger les services
      await modalRegistry.init('service-detail-modal-v2');
      
      // Attendre un peu pour s'assurer que les services sont chargés
      await new Promise(resolve => setTimeout(resolve, 100));
      
      this.migrationMode = 'parallel';
      console.log('✅ Mode parallèle activé - V1 et V2 coexistent');
      return true;
    } catch (error) {
      console.error('❌ Erreur lors de l\'activation du mode parallèle:', error);
      return false;
    }
  }

  /**
   * Migre complètement vers le nouveau système
   */
  public static async migrateToV2(): Promise<boolean> {
    console.log('🔄 Migration complète vers le système V2...');
    
    try {
      // Importer le nouveau système
      const { modalRegistry } = await import('./ModalRegistry');
      const { ServiceModal } = await import('./ServiceModal');
      
      // Fermer et supprimer les anciennes modales si elles existent
      const oldModal = document.getElementById('service-detail-modal');
      if (oldModal) {
        oldModal.remove();
      }

      // Créer et enregistrer la nouvelle modale avec l'ID original
      const serviceModal = new ServiceModal({ id: 'service-detail-modal' });
      modalRegistry.register(serviceModal, 'service');
      modalRegistry.init('service-detail-modal');

      this.migrationMode = 'full';
      console.log('✅ Migration complète terminée - Système V2 actif');
      return true;
    } catch (error) {
      console.error('❌ Erreur lors de la migration complète:', error);
      return false;
    }
  }

  /**
   * Rollback vers l'ancien système
   */
  public static async rollbackToV1(): Promise<boolean> {
    console.log('🔄 Rollback vers le système V1...');
    
    try {
      // Nettoyer le nouveau système
      const { modalRegistry } = await import('./ModalRegistry');
      modalRegistry.destroy();
      
      // Supprimer les éléments V2
      ['service-detail-modal-v2', 'service-detail-modal'].forEach(id => {
        const element = document.getElementById(id);
        if (element) {
          element.remove();
        }
      });

      this.initialized = false;
      this.migrationMode = 'test';
      
      console.log('✅ Rollback terminé - Système V1 restauré');
      return true;
    } catch (error) {
      console.error('❌ Erreur lors du rollback:', error);
      return false;
    }
  }

  /**
   * Ouvre une modale de service (compatible V1/V2)
   */
  public static async openService(serviceId: string): Promise<boolean> {
    try {
      // Si pas encore initialisé, initialiser en production
      if (!this.initialized) {
        await this.initProduction();
      }
      
      // Utiliser le système en production
      const { modalRegistry } = await import('./ModalRegistry');
      return modalRegistry.open('service-detail-modal', { serviceId });
    } catch (error) {
      console.error('Erreur lors de l\'ouverture de la modale:', error);
      return false;
    }
  }

  /**
   * Ferme toutes les modales
   */
  public static async closeAll(): Promise<void> {
    try {
      if (this.initialized) {
        const { modalRegistry } = await import('./ModalRegistry');
        modalRegistry.closeAll();
      }
    } catch (error) {
      console.error('Erreur lors de la fermeture des modales:', error);
    }
  }

  /**
   * État du système
   */
  public static getStatus(): { initialized: boolean; mode: string } {
    return {
      initialized: this.initialized,
      mode: this.migrationMode
    };
  }
}
